import { parseProjectPhase, ProjectPhase } from "../../../../../../constants/project-phase";
import { WorkflowStepModel } from "../../../../../../models";
import type { GetWorkflowTemplateResponse } from "../../../../../../service/query/Types";
import {
  getWorkflowStageKey,
  getWorkflowStageKeys,
  jsonReviver,
  normaliseWorkflowTemplatePhases as sharedNormaliseWorkflowTemplatePhases,
  stringKeyToSentenceCase,
  type WorkflowTemplatePhaseItem as SharedWorkflowTemplatePhaseItem,
} from "../../../../../../utils";
import { DealStageItem } from "../../../../../asset-manager/deal/components";

export type DealDatesRecord = Record<string, Record<string, unknown> | null>;
export type WorkflowTemplatePhaseItem = SharedWorkflowTemplatePhaseItem;

export const toStageLabel = (value: string): string => stringKeyToSentenceCase(value);

export const isInProgressStatus = (value: string | null | undefined): boolean =>
  value?.toLowerCase().replace(/[^a-z]/g, "") === "inprogress";

export const normaliseWorkflowTemplatePhases = (
  phases: GetWorkflowTemplateResponse["phases"] | undefined
): WorkflowTemplatePhaseItem[] => sharedNormaliseWorkflowTemplatePhases(phases);

export const getOrderedDealStageKeys = (stageKeys: string[], orderedPhaseNames: string[] = []): string[] => {
  return getWorkflowStageKeys(
    stageKeys,
    orderedPhaseNames.map((phase) => ({
      phase,
    }))
  );
};

const parseDealDatesRecord = (dealDates: unknown): Record<string, unknown> => {
  if (!dealDates) return {};

  const parsedDealDates =
    typeof dealDates === "string"
      ? (() => {
          try {
            return JSON.parse(dealDates) as Record<string, unknown>;
          } catch {
            return {};
          }
        })()
      : (dealDates as Record<string, unknown>);

  if (!parsedDealDates || typeof parsedDealDates !== "object") return {};
  return parsedDealDates;
};

export const parseDealStages = (
  dealDates: unknown,
  workflowTemplatePhasesOrOrderedPhaseNames: WorkflowTemplatePhaseItem[] | string[] = []
): DealStageItem[] => {
  const parsedDealDates = parseDealDatesRecord(dealDates);

  const workflowTemplatePhases =
    typeof workflowTemplatePhasesOrOrderedPhaseNames[0] === "string"
      ? (workflowTemplatePhasesOrOrderedPhaseNames as string[]).map((phase) => ({
          keyDates: [],
          phase,
          sequence: 0,
        }))
      : (workflowTemplatePhasesOrOrderedPhaseNames as WorkflowTemplatePhaseItem[]);

  return getWorkflowStageKeys(Object.keys(parsedDealDates), workflowTemplatePhases).map((key) => {
    const stage = parsedDealDates[key] as { Status?: string | null; status?: string | null } | undefined;
    const matchingWorkflowTemplatePhase = workflowTemplatePhases.find(
      (phaseItem) => getWorkflowStageKey(phaseItem.phase) === key
    );

    return {
      key,
      label: toStageLabel(matchingWorkflowTemplatePhase?.phase ?? key),
      status: stage?.Status ?? stage?.status ?? null,
    };
  });
};

export const getDefaultDealStage = (
  parsedDealStages: DealStageItem[],
  currentDealStage: string | undefined,
  fallbackDealStage: string | null | undefined,
  fallbackWorkflowStage: string | null | undefined
): string | undefined => {
  const selectedStageKey = getWorkflowStageKey(currentDealStage);
  const selectedStage = currentDealStage ? parsedDealStages.find((stage) => stage.key === selectedStageKey) : undefined;

  if (selectedStage) {
    return selectedStage.key;
  }

  const inProgressStage = parsedDealStages.find((stage) => isInProgressStatus(stage.status));
  if (inProgressStage) {
    return inProgressStage.key;
  }

  const fallbackStageKey = getWorkflowStageKey(fallbackDealStage);
  const fallbackMatchedStage = parsedDealStages.find((stage) => stage.key === fallbackStageKey);

  if (fallbackMatchedStage) {
    return fallbackMatchedStage.key;
  }

  const fallbackWorkflowStageKey = getWorkflowStageKey(fallbackWorkflowStage);

  if (parsedDealStages[0]?.key) {
    return parsedDealStages[0].key;
  }

  return fallbackWorkflowStageKey || undefined;
};

export const parseDealDates = (dealDates: unknown): DealDatesRecord | undefined => {
  if (!dealDates) return undefined;

  if (typeof dealDates === "string") {
    try {
      return JSON.parse(dealDates, jsonReviver) as DealDatesRecord;
    } catch {
      return undefined;
    }
  }

  if (typeof dealDates === "object") {
    return dealDates as DealDatesRecord;
  }

  return undefined;
};

export const filterWorkflowSteps = (
  steps: WorkflowStepModel[] | undefined,
  phaseOrStage: string | undefined
): WorkflowStepModel[] => {
  if (!steps || !phaseOrStage) return [];

  const targetStageKey = getWorkflowStageKey(phaseOrStage);

  return steps
    .filter((step) => getWorkflowStageKey(step.phase) === targetStageKey)
    .sort((a, b) => a.sequence - b.sequence);
};

export const parseProjectCachedPhase = (projectCachedPhase: string | undefined): ProjectPhase | undefined => {
  return parseProjectPhase(projectCachedPhase);
};
